// src/pages/BusinessAnalystDashboard.tsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  FileText,
  Bell,
  ClipboardCheck,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Database,
  Plus,
  Loader2,
  Search,
  Eye,
  Building2,
  MapPin,
  RefreshCw,
  ChevronRight,
  BarChart3,
  Target,
  Users,
  Award,
  Calendar,
  Wifi,
  WifiOff
} from 'lucide-react';

const formatCurrency = (value: number): string => {
  if (!value) return 'KES 0';
  if (value >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `KES ${(value / 1_000).toFixed(1)}K`;
  return `KES ${value.toLocaleString()}`;
};

export default function BusinessAnalystDashboard() {
  const { user } = useAuth();
  const { pitches } = useData();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [assignedProblems, setAssignedProblems] = useState<any[]>([]);
  const [availableProblems, setAvailableProblems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'assigned' | 'available'>('assigned');
  
  // Real-time connection status
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Refs for subscriptions
  const subscriptionsRef = useRef<any[]>([]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Load problems assigned to squads (for tracking)
      const { data: assigned } = await supabase
        .from('problem_bank')
        .select('*')
        .eq('status', 'assigned')
        .order('created_at', { ascending: false });

      const parsedAssigned = (assigned || []).map(problem => ({
        ...problem,
        anchor: typeof problem.anchor === 'string' ? JSON.parse(problem.anchor) : problem.anchor,
        quantification: typeof problem.quantification === 'string' ? JSON.parse(problem.quantification) : problem.quantification,
        assignment: typeof problem.assignment === 'string' ? JSON.parse(problem.assignment) : problem.assignment,
        priorityScore: problem.priority_score,
        problemId: problem.problem_id
      }));

      setAssignedProblems(parsedAssigned);

      // Load available problems (verified, not assigned)
      const { data: available } = await supabase
        .from('problem_bank')
        .select('*')
        .eq('status', 'verified')
        .order('created_at', { ascending: false });

      const parsedAvailable = (available || []).map(problem => ({
        ...problem,
        anchor: typeof problem.anchor === 'string' ? JSON.parse(problem.anchor) : problem.anchor,
        quantification: typeof problem.quantification === 'string' ? JSON.parse(problem.quantification) : problem.quantification,
        priorityScore: problem.priority_score,
        problemId: problem.problem_id
      }));

      setAvailableProblems(parsedAvailable);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh function
  const refreshData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await loadData();
      toast({
        title: "Data Refreshed",
        description: "All dashboard data has been updated.",
      });
    } catch (error) {
      console.error('Refresh failed:', error);
      toast({
        title: "Refresh Failed",
        description: "Could not refresh data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [loadData, toast]);

  // Setup real-time subscriptions
  useEffect(() => {
    if (!user) return;

    // Initial load
    loadData();

    // Function to setup subscriptions
    const setupSubscriptions = () => {
      // Clean up existing subscriptions
      subscriptionsRef.current.forEach(sub => {
        try {
          sub.unsubscribe();
        } catch (e) {
          console.warn('Error unsubscribing:', e);
        }
      });
      subscriptionsRef.current = [];

      // Problem Bank subscription - for both assigned and available problems
      const problemBankSub = supabase
        .channel('problem_bank_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'problem_bank'
          },
          (payload) => {
            console.log('Problem bank changed:', payload.eventType, payload.new?.id || payload.old?.id);
            // Debounce the refresh
            setTimeout(() => loadData(), 100);
          }
        )
        .subscribe((status) => {
          console.log('Problem Bank subscription status:', status);
          if (status === 'SUBSCRIBED') {
            setIsRealtimeConnected(true);
          } else if (status === 'CHANNEL_ERROR') {
            setIsRealtimeConnected(false);
          }
        });

      subscriptionsRef.current = [problemBankSub];
    };

    setupSubscriptions();

    // Polling fallback (every 30 seconds in case real-time fails)
    const intervalId = setInterval(() => {
      if (!isRealtimeConnected) {
        console.log('Real-time disconnected, polling for updates...');
        loadData();
      }
    }, 30000);

    // Cleanup on unmount
    return () => {
      clearInterval(intervalId);
      subscriptionsRef.current.forEach(sub => {
        try {
          sub.unsubscribe();
        } catch (e) {
          console.warn('Error unsubscribing:', e);
        }
      });
    };
  }, [user, loadData, isRealtimeConnected]);

  const calculateProgress = (problem: any): number => {
    if (!problem.assignment?.milestones) return 0;
    const completed = problem.assignment.milestones.filter((m: any) => m.completed).length;
    return Math.round((completed / problem.assignment.milestones.length) * 100);
  };

  const getDaysRemaining = (problem: any): number => {
    if (!problem.assignment?.targetDeliveryDate) return 0;
    const target = new Date(problem.assignment.targetDeliveryDate);
    const now = new Date();
    const diffTime = target.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getDashboardStats = () => {
    const assignedPitches = pitches.filter(p => p.assignedAnalystId === user?.id && !p.deletedAt);
    const pendingAnalysis = pitches.filter(p => p.assignedAnalystId === user?.id && !p.deletedAt && p.stage === 'analysis').length;
    const completedAnalyses = pitches.filter(p => p.assignedAnalystId === user?.id && !p.deletedAt && p.analysisScores !== null).length;

    return {
      totalPitches: assignedPitches.length,
      pendingAnalysis,
      completedAnalyses,
      assignedProblemsCount: assignedProblems.length,
      availableProblemsCount: availableProblems.length
    };
  };

  const stats = getDashboardStats();
  const assignedPitches = pitches.filter(p => p.assignedAnalystId === user?.id && !p.deletedAt).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  const pendingTasks = pitches.filter(p => p.assignedAnalystId === user?.id && !p.deletedAt && p.stage === 'analysis' && !p.analysisScores).length;

  const filteredAssignedProblems = assignedProblems.filter(problem => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (problem.title?.toLowerCase().includes(query) || problem.problemId?.toLowerCase().includes(query) || problem.anchor?.name?.toLowerCase().includes(query));
  });

  const filteredAvailableProblems = availableProblems.filter(problem => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (problem.title?.toLowerCase().includes(query) || problem.problemId?.toLowerCase().includes(query) || problem.anchor?.name?.toLowerCase().includes(query));
  });

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Business Analyst Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            You have {pendingTasks} pitch{pendingTasks !== 1 ? 'es' : ''} pending review. {stats.assignedProblemsCount} problems assigned to squads.
          </p>
          <div className="flex items-center gap-3 mt-2">
            <p className="text-xs text-muted-foreground">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
            <div className="flex items-center gap-1">
              {isRealtimeConnected ? (
                <>
                  <Wifi className="h-3 w-3 text-green-500" />
                  <span className="text-xs text-green-600">Live</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-3 w-3 text-red-500" />
                  <span className="text-xs text-red-600">Reconnecting...</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button onClick={() => navigate('/analyst/pitches')} variant="default">
            <ClipboardCheck className="h-4 w-4 mr-2" /> View Assigned Pitches
          </Button>
          <Button variant="outline" onClick={refreshData} disabled={isRefreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Assigned Pitches" value={stats.totalPitches.toString()} description="Ready for review" icon={ClipboardCheck} color="bg-blue-500" />
        <StatCard title="Pending Analysis" value={stats.pendingAnalysis.toString()} description="Need your attention" icon={Clock} color="bg-yellow-500" />
        <StatCard title="Completed Analyses" value={stats.completedAnalyses.toString()} description="Analyses finished" icon={CheckCircle} color="bg-green-500" />
        <StatCard title="Active Squad Problems" value={stats.assignedProblemsCount.toString()} description="Problems being worked on" icon={Users} color="bg-purple-500" />
      </div>

      {/* Pitch Cards */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              Needs Analysis
            </CardTitle>
            <CardDescription>Pitches awaiting your review</CardDescription>
          </CardHeader>
          <CardContent>
            {assignedPitches.filter(p => !p.analysisScores && p.stage === 'analysis').length > 0 ? (
              <div className="space-y-3">
                {assignedPitches.filter(p => !p.analysisScores && p.stage === 'analysis').slice(0, 5).map(pitch => (
                  <div 
                    key={pitch.id} 
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer" 
                    onClick={() => navigate(`/analyst/pitch/${pitch.id}?tab=analysis`)}
                  >
                    <div>
                      <p className="font-medium">{pitch.companyName}</p>
                      <p className="text-sm text-muted-foreground">{pitch.founderId}</p>
                    </div>
                    <Badge variant="outline" className="bg-yellow-50">Pending</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p>No pitches pending review</p>
                <p className="text-sm">All caught up!</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Recently Analyzed
            </CardTitle>
            <CardDescription>Pitches you've completed</CardDescription>
          </CardHeader>
          <CardContent>
            {assignedPitches.filter(p => p.analysisScores).length > 0 ? (
              <div className="space-y-3">
                {assignedPitches.filter(p => p.analysisScores).slice(0, 5).map(pitch => (
                  <div 
                    key={pitch.id} 
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer" 
                    onClick={() => navigate(`/analyst/pitch/${pitch.id}`)}
                  >
                    <div>
                      <p className="font-medium">{pitch.companyName}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs">Score:</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          pitch.analysisScores?.overall >= 70 ? 'bg-green-100 text-green-700' : 
                          pitch.analysisScores?.overall >= 50 ? 'bg-yellow-100 text-yellow-700' : 
                          'bg-red-100 text-red-700'
                        }`}>
                          {pitch.analysisScores?.overall}/100
                        </span>
                      </div>
                    </div>
                    <Badge>{pitch.stage}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p>No analyses completed yet</p>
                <p className="text-sm">Start reviewing assigned pitches</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Problems Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="space-y-4 w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="assigned">Assigned Problems</TabsTrigger>
          <TabsTrigger value="available">Available Problems</TabsTrigger>
        </TabsList>

        <TabsContent value="assigned" className="space-y-4 w-full">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Assigned to Squads
                </CardTitle>
                <CardDescription>Problems currently being worked on by squads</CardDescription>
              </div>
              <div className="flex gap-2">
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search..." 
                    value={searchQuery} 
                    onChange={(e) => setSearchQuery(e.target.value)} 
                    className="pl-9" 
                  />
                </div>
                <Button variant="outline" size="sm" onClick={refreshData} disabled={isRefreshing}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
                  <p className="text-muted-foreground mt-2">Loading problems...</p>
                </div>
              ) : filteredAssignedProblems.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground">No problems assigned to squads yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Problem</TableHead>
                        <TableHead>Assigned To</TableHead>
                        <TableHead>Progress</TableHead>
                        <TableHead>Target Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAssignedProblems.map((problem) => {
                        const progress = calculateProgress(problem);
                        const daysRemaining = getDaysRemaining(problem);
                        return (
                          <TableRow key={problem.id}>
                            <TableCell className="font-mono text-xs">{problem.problemId}</TableCell>
                            <TableCell>
                              <p className="font-medium">{problem.title}</p>
                              <div className="text-xs text-muted-foreground">{problem.anchor?.name}</div>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-purple-100 text-purple-700">{problem.assigned_to_squad_name || 'Unknown'}</Badge>
                            </TableCell>
                            <TableCell className="min-w-[100px]">
                              <div className="space-y-1">
                                <div className="flex justify-between text-xs">
                                  <span>{progress}%</span>
                                  <span>{daysRemaining > 0 ? `${daysRemaining}d left` : 'Due soon'}</span>
                                </div>
                                <Progress value={progress} className="h-1.5" />
                              </div>
                            </TableCell>
                            <TableCell>
                              {problem.assignment?.targetDeliveryDate ? new Date(problem.assignment.targetDeliveryDate).toLocaleDateString() : 'Not set'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="outline" size="sm" onClick={() => navigate(`/problem-bank/problem/${problem.id}/progress`)}>
                                <BarChart3 className="h-3 w-3 mr-1" />
                                Track
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="available" className="space-y-4 w-full">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Available Problems
                </CardTitle>
                <CardDescription>Verified problems ready for squad assignment</CardDescription>
              </div>
              <div className="flex gap-2">
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search..." 
                    value={searchQuery} 
                    onChange={(e) => setSearchQuery(e.target.value)} 
                    className="pl-9" 
                  />
                </div>
                <Button variant="outline" size="sm" onClick={refreshData} disabled={isRefreshing}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
                  <p className="text-muted-foreground mt-2">Loading problems...</p>
                </div>
              ) : filteredAvailableProblems.length === 0 ? (
                <div className="text-center py-12">
                  <Database className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground">No available problems</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Problem / Company</TableHead>
                        <TableHead>Loss (KES/yr)</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAvailableProblems.map((problem) => (
                        <TableRow key={problem.id}>
                          <TableCell className="font-mono text-xs">{problem.problemId}</TableCell>
                          <TableCell>
                            <p className="font-medium">{problem.title}</p>
                            <div className="text-xs text-muted-foreground">{problem.anchor?.name}</div>
                          </TableCell>
                          <TableCell className="text-red-600">{formatCurrency(problem.quantification?.totalLossPerYear || 0)}</TableCell>
                          <TableCell>
                            {problem.priorityScore ? (
                              <Badge className={problem.priorityScore.priorityTier === 'critical' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}>
                                {problem.priorityScore.priorityTier.toUpperCase()}
                              </Badge>
                            ) : (
                              <Badge variant="outline">-</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button 
                              variant="default" 
                              size="sm" 
                              onClick={() => navigate(`/problem-bank/problem/${problem.id}/squads`)} 
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <Users className="h-3 w-3 mr-1" />
                              Assign to Squad
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2" 
              onClick={() => navigate('/analyst/pitches')}
            >
              <ClipboardCheck className="h-6 w-6" />
              <span>All Pitches</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2" 
              onClick={() => navigate('/analyst/pitches?stage=analysis')}
            >
              <Clock className="h-6 w-6 text-yellow-500" />
              <span>Pending Pitches</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2" 
              onClick={() => navigate('/problem-bank')}
            >
              <Database className="h-6 w-6" />
              <span>Problem Bank</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2" 
              onClick={() => navigate('/problem-bank/my-assignments')}
            >
              <Target className="h-6 w-6" />
              <span>Squad Assignments</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2" 
              onClick={() => navigate('/notifications')}
            >
              <Bell className="h-6 w-6" />
              <span>Notifications</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ title, value, description, icon: Icon, color }: any) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          </div>
          <div className={`${color} p-3 rounded-full text-white`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}